// frontend/src/lib/reveal-request-status.ts
import type { BadgeVariant } from '@/components/badge';

import type { RevealRequest } from './api';

export interface RevealRequestStatusDescription {
  label: string;
  detail: string;
  canAct: boolean;
  // Which Badge variant this status renders as (profile-view's
  // ProfileRevealStatus, reveal-requests's OutgoingRequestRow/
  // DeclinedRequestRow) -- computed here, alongside label/detail,
  // rather than left for each call site to re-derive from `status`/
  // `canAct` independently, so the exact same request can never render
  // two different badge colors on two different screens. Token-by-
  // intent mapping: 'success' for an actually-achieved connection
  // (mutual), 'error' for a negative resolved outcome (declined),
  // 'primary' for a request that needs THIS viewer's action
  // (incoming, canAct true), 'neutral' for passive waiting (outgoing,
  // canAct false) -- 'warning' is intentionally unused here, since
  // nothing about a reveal-request status is a caution/risk state.
  variant: BadgeVariant;
}

// Pure: given a RevealRequest and which side of it the current user is
// on, what should the UI show? Extracted so this branching (four
// backend statuses x two viewer roles) is unit-testable without
// rendering a screen, matching use-tier0-state.ts's deriveTier0Phase
// precedent.
export function describeRevealRequestStatus(rr: RevealRequest, currentUserId: string): RevealRequestStatusDescription {
  const isRequester = rr.requesterId === currentUserId;

  if (rr.status === 'requested') {
    return isRequester
      ? { label: 'Pending', detail: "You're waiting for them to respond.", canAct: false, variant: 'neutral' }
      : {
          label: 'Requested you',
          detail: 'They want to reveal identities with you.',
          canAct: true,
          variant: 'primary',
        };
  }

  if (rr.status === 'mutual') {
    // Review pass on #116 tightened this twice: the old copy ("You can
    // now see each other clearly.") promised an identity reveal the
    // product doesn't perform, and even "full profiles" overclaimed --
    // the pseudonymous profile was already viewable pre-mutual (a
    // design.md-recorded decision), and photos are empty until the
    // upload pipeline exists (#135/#136). What mutual changes today is
    // the connection itself; say that.
    //
    // "Direct messaging isn't here yet" was accurate when #64 was an
    // undated P2 -- it shipped this session (PR #323), and a real user
    // hit this exact stale claim live in production. Point at the real
    // feature instead of denying it exists; profile-view.tsx's own
    // ProfileRevealStatus adds the actual "Message" button (it needs
    // the pair's connectionId, which this pure function has no access
    // to -- RevealRequest carries no connection reference).
    return {
      label: 'Connected',
      detail: "You're connected.",
      canAct: false,
      variant: 'success',
    };
  }

  // status === 'declined'
  return isRequester
    ? { label: 'Declined', detail: 'They declined this request.', canAct: false, variant: 'error' }
    : { label: 'Declined', detail: 'You declined this request.', canAct: false, variant: 'error' };
}

// Pure: given "now" and a resolved-declined request's server-computed
// cooldownUntil, is the requester still inside the backend's decline
// cooldown, and if so, what should the UI say? Returns undefined once
// the cooldown has elapsed (nothing to show) or if cooldownUntil is
// absent (request isn't declined, or isn't resolved yet). Never
// implies an immediate retry works when the backend will 429 it --
// issue #16's own explicit acceptance criterion.
//
// Issue #151 AC6b: this used to re-derive the cooldown's END time
// itself, from resolvedAt plus a hardcoded DECLINE_COOLDOWN_MS mirror
// of the backend's own declineCooldown constant -- doubling as a
// second place that duration lived, and (worse) a drift risk if either
// side's constant ever changed alone. cooldownUntil is the backend's
// OWN computed endpoint (reveal_request.go's toRevealRequestResponse:
// resolvedAt + declineCooldown), so this function no longer needs to
// know the duration at all -- only compare it against `now`, which is
// necessarily still a live client-side value for a countdown that
// updates as time passes, but the END time itself is server-owned.
// cooldownUntil: string | null | undefined -- issue #143's switch to the
// generated OpenAPI type widened this from `string | undefined`. The
// `null` case was already handled correctly (`!cooldownUntil` below
// catches it same as undefined/empty string); only the signature needed
// updating to match what the schema actually allows.
export function describeCooldown(now: Date, cooldownUntil: string | null | undefined): string | undefined {
  if (!cooldownUntil) return undefined;

  const remainingMs = new Date(cooldownUntil).getTime() - now.getTime();
  if (remainingMs <= 0) return undefined;

  const hoursLeft = Math.ceil(remainingMs / (60 * 60 * 1000));
  return `You'll need to wait about ${hoursLeft} hour${hoursLeft === 1 ? '' : 's'} before requesting this again.`;
}

// Issue #102: profile-view rendered a declined reveal-request as a
// permanent card and never re-offered "Request reveal", even once the
// backend's own cooldown (reveal_request.go's declineCooldown) had
// elapsed and a fresh POST would succeed. Only the ORIGINAL REQUESTER
// is ever cooldown-gated -- recipientRequestable's LatestDeclineAt
// check is keyed (requesterID, recipientID) directionally, so the
// person who declined was never blocked from anything here in the
// first place; this only re-opens the button for the side the backend
// actually restricted.
export function canReRequestAfterDecline(rr: RevealRequest, currentUserId: string, now: Date): boolean {
  if (rr.status !== 'declined') return false;
  if (rr.requesterId !== currentUserId) return false;
  return describeCooldown(now, rr.cooldownUntil) === undefined;
}
